import { CSSProperties, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay } from "@fortawesome/free-solid-svg-icons";

interface VideoThumbProps {
  /** Video source (signed URL or a blob: URL for a transient upload). */
  src: string;
  /** Applied to the wrapper (size, border, margins). */
  style?: CSSProperties;
  className?: string;
  /** Opens the expanded/fullscreen preview. */
  onClick?: () => void;
  title?: string;
  /** Corner "play" badge so a moving thumbnail still reads as a video. */
  badge?: boolean;
  /** Wrapper border radius (px). */
  radius?: number;
  testId?: string;
}

/**
 * PROG-MEDIA/ANIM — a moving video thumbnail. Renders the clip inline, muted +
 * autoplay + loop (the only combination browsers allow to play without a user
 * gesture), so the animation is visible in the anteprima and not only when the
 * media is expanded — mirroring the GIF behavior of an <img> thumbnail. The
 * <video> is pointer-transparent so a click falls through to the wrapper's
 * onClick (which opens the fullscreen modal).
 */
export default function VideoThumb({
  src,
  style,
  className,
  onClick,
  title,
  badge = true,
  radius = 8,
  testId,
}: VideoThumbProps) {
  // React doesn't always reflect the `muted` prop to the DOM attribute that the
  // browser's autoplay policy checks, so muted-autoplay can be blocked. Force
  // it on the element and kick off playback via a ref callback.
  const setVideoRef = useCallback((el: HTMLVideoElement | null) => {
    if (!el) return;
    el.muted = true;
    const p = el.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  }, []);

  return (
    <div
      className={className}
      onClick={onClick}
      title={title}
      data-testid={testId}
      style={{
        position: "relative",
        overflow: "hidden",
        background: "#1f1f1f",
        cursor: onClick ? "pointer" : undefined,
        borderRadius: radius,
        ...style,
      }}
    >
      <video
        ref={setVideoRef}
        src={src}
        muted
        autoPlay
        loop
        playsInline
        preload="metadata"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          pointerEvents: "none",
        }}
      />
      {badge && (
        <span
          style={{
            position: "absolute",
            right: 3,
            bottom: 3,
            width: 16,
            height: 16,
            borderRadius: "50%",
            background: "rgba(0, 0, 0, 0.6)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 8,
            pointerEvents: "none",
          }}
        >
          <FontAwesomeIcon icon={faPlay} />
        </span>
      )}
    </div>
  );
}
